import data from "@/app/data/loan/percentile-taea.json"

export type Smoker = "F" | "NF"
export type WorkStatus = "CAD" | "NCA" | "MAN"
export type CostMode = "taux" | "euro"
export type Verdict = "ok" | "mid" | "warn" | "hot"

export interface ReferenceCell {
    key: string
    n: number
    q: number[]
    label: string
}

export interface PercentilePosition {
    p: number
    floor: boolean
    ceil: boolean
}

export interface PercentileFormInput {
    age: string
    smoker: Smoker | null
    status: WorkStatus | null
    capital: string
    years: string
    mode: CostMode
    rate: string
    monthly: string
}

export interface PercentileResult {
    cell: ReferenceCell
    position: PercentilePosition
    shown: number
    verdict: Verdict
    median: number
    bestFifth: number
    overcost: number
    overcostBestFifth: number
}

export interface PercentileForm {
    age: number | null
    smoker: Smoker | null
    status: WorkStatus | null
    capital: number | null
    years: number | null
    rate: number | null
    euroOutOfRange: boolean
    missing: string[]
}

export const TOOL_META = data.meta
export const PERCENTILES: number[] = data.meta.percentiles

const GRID: Record<string, number[]> = data.grid
const MAX_RATE = 3
const MEDIAN_INDEX = PERCENTILES.indexOf(50)
const BEST_FIFTH_INDEX = PERCENTILES.indexOf(20)

const AGE_BANDS: [number, string][] = [
    [30, "18-29"],
    [35, "30-34"],
    [40, "35-39"],
    [45, "40-44"],
    [50, "45-49"],
    [55, "50-54"],
    [60, "55-59"],
    [65, "60-64"],
]

// Smokers are only 7.4 % of the base, too few to fill a 5-year band after 50
const BROAD_AGE_BANDS: [number, string][] = [
    [40, "18-39"],
    [50, "40-49"],
]

const AGE_LABELS: Record<string, string> = {
    "18-29": "18-29 ans",
    "30-34": "30-34 ans",
    "35-39": "35-39 ans",
    "40-44": "40-44 ans",
    "45-49": "45-49 ans",
    "50-54": "50-54 ans",
    "55-59": "55-59 ans",
    "60-64": "60-64 ans",
    "65+": "65 ans et plus",
    "18-39": "18-39 ans",
    "40-49": "40-49 ans",
    "50+": "50 ans et plus",
}

const SMOKER_LABELS: Record<Smoker, string> = { F: "fumeur", NF: "non-fumeur" }

const STATUS_LABELS: Record<WorkStatus, string> = {
    CAD: "cadre / libéral",
    NCA: "non cadre",
    MAN: "métier manuel / indépendant",
}

const DURATION_LABELS: Record<string, string> = { S: "moins de 16 ans", M: "16 à 22 ans", L: "23 ans et plus" }

type Dimension = "age" | "broadAge" | "smoker" | "status" | "duration"

// Least discriminating dimension dropped first
const LADDER: { level: string; min: number; dims: Dimension[] }[] = [
    { level: "ASTD", min: 60, dims: ["age", "smoker", "status", "duration"] },
    { level: "AST", min: 60, dims: ["age", "smoker", "status"] },
    { level: "ASD", min: 60, dims: ["age", "smoker", "duration"] },
    { level: "AS", min: 40, dims: ["age", "smoker"] },
    { level: "BS", min: 40, dims: ["broadAge", "smoker"] },
    { level: "A", min: 40, dims: ["age"] },
]

const ALL_KEY = "ALL|*|*|*|*"

const band = (value: number, bands: [number, string][], last: string): string =>
    bands.find(([limit]) => value < limit)?.[1] ?? last

export const ageBand = (age: number): string => band(age, AGE_BANDS, "65+")
export const broadAgeBand = (age: number): string => band(age, BROAD_AGE_BANDS, "50+")
export const durationBand = (years: number): string => (years < 16 ? "S" : years < 23 ? "M" : "L")

const cellFromRow = (key: string, row: number[], label: string): ReferenceCell => ({
    key,
    n: row[0],
    q: row.slice(1),
    label,
})

export const allProfilesCell = (): ReferenceCell => cellFromRow(ALL_KEY, GRID[ALL_KEY], "tous profils")

export const pickCell = (age: number, smoker: Smoker, status: WorkStatus, years: number): ReferenceCell => {
    const values: Record<Dimension, string> = {
        age: ageBand(age),
        broadAge: broadAgeBand(age),
        smoker,
        status,
        duration: durationBand(years),
    }

    for (const { level, min, dims } of LADDER) {
        // After 50, a mixed population halves a smoker's reference: never drop the smoking status for them
        if (smoker === "F" && !dims.includes("smoker")) continue

        const has = (dim: Dimension) => dims.includes(dim)
        const key = [
            level,
            has("age") ? values.age : has("broadAge") ? values.broadAge : "*",
            has("smoker") ? smoker : "*",
            has("status") ? status : "*",
            has("duration") ? values.duration : "*",
        ].join("|")
        const row = GRID[key]
        if (!row || row[0] < min) continue

        const label = [
            has("age") ? AGE_LABELS[values.age] : has("broadAge") ? AGE_LABELS[values.broadAge] : null,
            has("smoker") ? SMOKER_LABELS[smoker] : null,
            has("status") ? STATUS_LABELS[status] : null,
            has("duration") ? `durée ${DURATION_LABELS[values.duration]}` : null,
        ]
            .filter(Boolean)
            .join(" · ")

        return cellFromRow(key, row, label)
    }

    return allProfilesCell()
}

export const percentileOf = (q: number[], rate: number): PercentilePosition => {
    const last = q.length - 1
    if (rate <= q[0]) return { p: PERCENTILES[0], floor: true, ceil: false }
    if (rate >= q[last]) return { p: PERCENTILES[last], floor: false, ceil: true }

    const i = q.findIndex((value, index) => index > 0 && rate <= value) - 1
    const span = q[i + 1] - q[i]
    const share = span > 0 ? (rate - q[i]) / span : 0

    return { p: PERCENTILES[i] + (PERCENTILES[i + 1] - PERCENTILES[i]) * share, floor: false, ceil: false }
}

export const shownPercentile = ({ p, floor, ceil }: PercentilePosition): number =>
    floor ? PERCENTILES[0] : ceil ? PERCENTILES[PERCENTILES.length - 1] : Math.round(p)

export const verdictOf = (p: number): Verdict => (p < 25 ? "ok" : p < 60 ? "mid" : p < 80 ? "warn" : "hot")

export const medianOf = (cell: ReferenceCell): number => cell.q[MEDIAN_INDEX]
export const bestFifthOf = (cell: ReferenceCell): number => cell.q[BEST_FIFTH_INDEX]

export const overcost = (rate: number, reference: number, capital: number, years: number): number =>
    Math.max(0, ((rate - reference) / 100) * capital * years)

export const parseNumber = (value: string): number | null => {
    const raw = value.replace(/[\s\u00a0\u202f]/g, "").replace(",", ".")
    return /^\d*\.?\d+$/.test(raw) ? Number(raw) : null
}

const inRange = (value: number | null, min: number, max: number): number | null =>
    value !== null && value >= min && value <= max ? value : null

export const monthlyToRate = (monthly: number, capital: number): number => ((monthly * 12) / capital) * 100
export const rateToMonthly = (rate: number, capital: number): number => ((rate / 100) * capital) / 12

export const readForm = (input: PercentileFormInput): PercentileForm => {
    const age = inRange(parseNumber(input.age), 18, 80)
    const capital = inRange(parseNumber(input.capital), 10000, 3000000)
    const years = inRange(parseNumber(input.years), 2, 35)

    let rate: number | null = null
    let euroOutOfRange = false
    if (input.mode === "euro") {
        const monthly = inRange(parseNumber(input.monthly), 1, 5000)
        if (monthly !== null && capital !== null) {
            const derived = monthlyToRate(monthly, capital)
            if (derived <= MAX_RATE) rate = derived
            else euroOutOfRange = true
        }
    } else {
        rate = inRange(parseNumber(input.rate), 0.005, MAX_RATE)
    }

    const missing = [
        age === null && "votre âge",
        input.smoker === null && "si vous fumez",
        input.status === null && "votre statut",
        capital === null && "le capital emprunté",
        years === null && "la durée restante",
        rate === null && !euroOutOfRange && (input.mode === "euro" ? "votre cotisation mensuelle" : "votre taux actuel"),
    ].filter((label): label is string => Boolean(label))

    return { age, smoker: input.smoker, status: input.status, capital, years, rate, euroOutOfRange, missing }
}

export const positionFor = ({ age, smoker, status, capital, years, rate }: PercentileForm): PercentileResult | null => {
    if (age === null || smoker === null || status === null || capital === null || years === null || rate === null) {
        return null
    }

    const cell = pickCell(age, smoker, status, years)
    const position = percentileOf(cell.q, rate)
    const median = medianOf(cell)
    const bestFifth = bestFifthOf(cell)

    return {
        cell,
        position,
        shown: shownPercentile(position),
        verdict: verdictOf(position.p),
        median,
        bestFifth,
        overcost: overcost(rate, median, capital, years),
        overcostBestFifth: overcost(rate, bestFifth, capital, years),
    }
}

export const listFr = (items: string[]): string =>
    items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`

export const niceTicks = (lo: number, hi: number, width: number): number[] => {
    const target = width < 460 ? 4 : 5
    const maxTicks = width < 460 ? 5 : 8
    let best: { step: number; first: number; last: number; score: number } | null = null

    for (let exponent = -4; exponent <= 1; exponent++) {
        for (const multiple of [1, 2, 2.5, 5]) {
            const step = multiple * Math.pow(10, exponent)
            const first = Math.ceil(lo / step)
            const last = Math.floor(hi / step)
            const count = last - first + 1
            if (count < 3 || count > maxTicks) continue

            const score = Math.abs(count - target)
            if (!best || score < best.score) best = { step, first, last, score }
        }
    }

    if (!best) return [lo, (lo + hi) / 2, hi]

    const ticks: number[] = []
    for (let k = best.first; k <= best.last; k++) ticks.push(+(k * best.step).toFixed(6))
    return ticks
}

const NBSP = " "
const numberFormat = new Intl.NumberFormat("fr-FR")

export const formatNumber = (value: number): string => numberFormat.format(value)
export const formatEur = (value: number): string => `${numberFormat.format(Math.round(value))}${NBSP}€`
export const formatRateValue = (value: number): string =>
    value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",")
export const formatRate = (value: number): string => `${formatRateValue(value)}${NBSP}%`
export const formatDecimal = (value: number): string => String(value).replace(".", ",")
export const formatIsoDate = (iso: string): string => iso.split("-").reverse().join("/")
// Clipboard text is pasted into places that may mangle narrow no-break spaces
export const plainNumber = (value: number): string => numberFormat.format(value).replace(/[  ]/g, " ")
